import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { AdMob, BannerAdSize, BannerAdPosition } from '@capacitor-community/admob';
import { Sentry } from '@/services/sentry';

// Ad Unit IDs
// To swap back to test IDs (e.g. for local testing via Capgo OTA), replace the
// interstitial values below with Google's test IDs:
//   iOS interstitial:     ca-app-pub-3940256099942544/4411468910
//   Android interstitial: ca-app-pub-3940256099942544/1033173712
const AD_UNITS = {
  ios: {
    // interstitial: 'ca-app-pub-4490658762714537/8406641341',
    interstitial: 'ca-app-pub-3940256099942544/4411468910',
  },
  android: {
    interstitial: 'ca-app-pub-4490658762714537/3201921280',
  },
};

const isAdMobInitialized = ref(false);
const isBannerVisible = ref(false);

// Frequency capping for interstitial ads
let lastInterstitialAdTime = 0;
const INTERSTITIAL_AD_COOLDOWN = 3 * 60 * 1000; // 3 minutes in milliseconds

// Click threshold: ad is shown only after this many clicks
let interstitialClickCount = 0;
const INTERSTITIAL_CLICK_THRESHOLD = 5; // show ad on the 6th click

/**
 * Request App Tracking Transparency authorization (iOS 14+)
 * Must be called before initializing AdMob for personalized ads
 */
async function requestTrackingAuthorization() {
  if (Capacitor.getPlatform() !== 'ios') {
    return;
  }

  try {
    const status = await AdMob.requestTrackingAuthorization();
    console.log('[AdMob] 🔐 Tracking authorization status:', status);
  } catch (error) {
    console.error('[AdMob] ❌ Failed to request tracking authorization:', error);
    Sentry.captureException(error);
  }
}

/**
 * Initialize AdMob
 * Call this once on app startup
 */
export async function initializeAdMob() {
  if (!Capacitor.isNativePlatform()) {
    console.log('[AdMob] ⚠️ Skipping initialization (not native platform)');
    return;
  }

  try {
    // Request ATT authorization first (iOS 14+)
    await requestTrackingAuthorization();

    await AdMob.initialize({
      initializeForTesting: false,
      // testingDevices: ['24BC010199D08E84779CD91E8AFA1CE5'],
    });
    isAdMobInitialized.value = true;
    console.log('[AdMob] ✅ Initialized successfully');
  } catch (error) {
    console.error('[AdMob] ❌ Initialization failed:', error);
    Sentry.captureException(error);
  }
}

/**
 * Get the appropriate ad unit ID for current platform
 */
function getAdUnitId(adType) {
  const platform = Capacitor.getPlatform();
  return AD_UNITS[platform]?.[adType] || AD_UNITS.android[adType];
}

/**
 * Show banner ad at bottom of screen
 * @param {boolean} isPremiumUser - If true, ad won't be shown
 */
export async function showBannerAd(isPremiumUser = false) {
  if (!Capacitor.isNativePlatform() || !isAdMobInitialized.value) {
    console.log('[AdMob] ⚠️ Cannot show banner (not initialized or not native)');
    return;
  }

  if (isPremiumUser) {
    console.log('[AdMob] ⚠️ User is premium, not showing banner ad');
    return;
  }

  try {
    // Get tab bar height to position banner above it
    const tabBarHeight = 120; // Ionic default tab bar height in pixels

    await AdMob.showBanner({
      adId: getAdUnitId('banner'),
      adSize: BannerAdSize.BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: tabBarHeight, // Push banner above tab bar
    });
    isBannerVisible.value = true;
    console.log('[AdMob] ✅ Banner ad shown');
  } catch (error) {
    console.error('[AdMob] ❌ Failed to show banner ad:', error);
    Sentry.captureException(error);
  }
}

/**
 * Hide banner ad
 */
export async function hideBannerAd() {
  if (!Capacitor.isNativePlatform() || !isBannerVisible.value) {
    return;
  }

  try {
    await AdMob.hideBanner();
    isBannerVisible.value = false;
    console.log('[AdMob] ✅ Banner ad hidden');
  } catch (error) {
    console.error('[AdMob] ❌ Failed to hide banner ad:', error);
    Sentry.captureException(error);
  }
}

/**
 * Remove banner ad completely
 */
export async function removeBannerAd() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await AdMob.removeBanner();
    isBannerVisible.value = false;
    console.log('[AdMob] ✅ Banner ad removed');
  } catch (error) {
    console.error('[AdMob] ❌ Failed to remove banner ad:', error);
    Sentry.captureException(error);
  }
}

/**
 * Pre-load interstitial ad in advance
 * Call this early (e.g., on page mount) so ad is ready to show instantly
 * @param {boolean} isPremiumUser - If true, ad won't be loaded
 */
export async function prepareInterstitialAd(isPremiumUser = false) {
  if (!Capacitor.isNativePlatform() || !isAdMobInitialized.value) {
    console.log('[AdMob] ⚠️ Cannot prepare interstitial (not initialized or not native)');
    return;
  }

  if (isPremiumUser) {
    console.log('[AdMob] ⚠️ User is premium, not preparing interstitial ad');
    return;
  }

  try {
    await AdMob.prepareInterstitial({
      adId: getAdUnitId('interstitial'),
    });
    console.log('[AdMob] ✅ Interstitial ad prepared and ready to show');
  } catch (error) {
    console.error('[AdMob] ❌ Failed to prepare interstitial ad:', error);
    Sentry.captureException(error);
  }
}

/**
 * Show interstitial ad (full screen)
 * Must call prepareInterstitialAd() first, or this will have delay
 * Uses time-based frequency capping (3 minutes) to avoid showing ads too often
 * @param {boolean} isPremiumUser - If true, ad won't be shown
 */
export async function showInterstitialAd(isPremiumUser = false) {
  if (!Capacitor.isNativePlatform() || !isAdMobInitialized.value) {
    console.log('[AdMob] ⚠️ Cannot show interstitial (not initialized or not native)');
    return;
  }

  if (isPremiumUser) {
    console.log('[AdMob] ⚠️ User is premium, not showing interstitial ad');
    return;
  }

  // Check if enough time has passed since last ad (frequency capping)
  const now = Date.now();
  const timeSinceLastAd = now - lastInterstitialAdTime;

  if (timeSinceLastAd < INTERSTITIAL_AD_COOLDOWN) {
    const remainingTime = Math.ceil((INTERSTITIAL_AD_COOLDOWN - timeSinceLastAd) / 1000);
    console.log(`[AdMob] ⏱️ Ad cooldown active. ${remainingTime}s remaining until next ad`);
    return;
  }

  // Increment click counter and check if threshold is reached
  interstitialClickCount++;
  if (interstitialClickCount <= INTERSTITIAL_CLICK_THRESHOLD) {
    console.log(`[AdMob] 🔢 Click ${interstitialClickCount}/${INTERSTITIAL_CLICK_THRESHOLD + 1} - ad will show on ${INTERSTITIAL_CLICK_THRESHOLD} click`);
    return;
  }

  try {
    // Show the ad (must be prepared first)
    await AdMob.showInterstitial();
    console.log('[AdMob] ✅ Interstitial ad shown');

    // Reset click counter and update last ad time
    interstitialClickCount = 0;
    lastInterstitialAdTime = now;

    // Pre-load next ad for next time
    prepareInterstitialAd(isPremiumUser);
  } catch (error) {
    console.error('[AdMob] ❌ Failed to show interstitial ad:', error);
    Sentry.captureException(error);
  }
}

/**
 * Show rewarded ad (user watches for reward)
 * Returns true if user completed watching the ad
 * @param {boolean} isPremiumUser - If true, ad won't be shown (returns true immediately)
 * @returns {Promise<boolean>} - True if user watched the ad completely
 */
export async function showRewardedAd(isPremiumUser = false) {
  if (!Capacitor.isNativePlatform() || !isAdMobInitialized.value) {
    console.log('[AdMob] ⚠️ Cannot show rewarded ad (not initialized or not native)');
    return false;
  }

  if (isPremiumUser) {
    console.log('[AdMob] ⚠️ User is premium, skipping rewarded ad');
    return true; // Premium users get reward without watching ad
  }

  try {
    // Prepare the ad
    await AdMob.prepareRewardVideoAd({
      adId: getAdUnitId('rewarded'),
    });

    // Show the ad and wait for result
    const result = await AdMob.showRewardVideoAd();
    console.log('[AdMob] ✅ Rewarded ad result:', result);

    // Return true if user watched the ad
    return result?.type === 'rewarded';
  } catch (error) {
    console.error('[AdMob] ❌ Failed to show rewarded ad:', error);
    Sentry.captureException(error);
    return false;
  }
}

/**
 * Add event listeners for ad events
 */
export function setupAdMobListeners() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  // Banner events
  AdMob.addListener('onAdLoaded', (info) => {
    console.log('[AdMob] 📢 Ad loaded', info);
  });

  AdMob.addListener('onAdFailedToLoad', (info) => {
    console.log('[AdMob] 📢 Ad failed to load', info);
  });

  AdMob.addListener('onAdOpened', (info) => {
    console.log('[AdMob] 📢 Ad opened', info);
  });

  AdMob.addListener('onAdClosed', (info) => {
    console.log('[AdMob] 📢 Ad closed', info);
  });
}

export function useAdMob() {
  return {
    isAdMobInitialized,
    isBannerVisible,
    initializeAdMob,
    showBannerAd,
    hideBannerAd,
    removeBannerAd,
    showInterstitialAd,
    showRewardedAd,
    setupAdMobListeners,
  };
}
